import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { Tender } from '../../types/tender';
import type { FirestoreTimestamp, UploadedAt } from './types';

// Type guard for Firestore Timestamp with toDate method
interface FirestoreTimestampWithToDate extends FirestoreTimestamp {
  toDate: () => Date;
}

const isFirestoreTimestampWithToDate = (value: unknown): value is FirestoreTimestampWithToDate => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as FirestoreTimestampWithToDate).toDate === 'function'
  );
};

// Helper to convert various timestamp formats to Date
export const toDate = (value: UploadedAt | undefined | null): Date => {
  if (!value) {
    return new Date();
  }
  if (isFirestoreTimestampWithToDate(value)) {
    return value.toDate();
  }
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000);
  }
  return new Date(value as string | number | Date);
};

// Process status type for cooldown calculation
interface ProcessStatus {
  startedAt?: UploadedAt;
  status?: string;
}

// Helper to calculate remaining cooldown minutes for a process
export const calculateResetCooldown = (
  process: ProcessStatus | undefined,
  cooldownMinutes: number
): number => {
  if (!process?.startedAt) {
    return 0;
  }

  const status = process.status?.toLowerCase() || '';
  const isComplete = status.includes('complete') || status.includes('success');
  const isError = status.includes('error') || status.includes('failed');

  if (isComplete || isError) {
    return 0;
  }

  const startedAt = toDate(process.startedAt);
  const cooldownMs = cooldownMinutes * 60 * 1000;
  const elapsed = Date.now() - startedAt.getTime();

  if (elapsed >= cooldownMs) {
    return 0;
  }

  return Math.ceil((cooldownMs - elapsed) / 60000);
};

// Helper to check if a service status string represents an active/in-progress generation state
export const isServiceProcessingStatus = (status: string | undefined | null): boolean => {
  if (!status) {
    return false;
  }
  const lower = status.toLowerCase().trim();
  if (!lower || lower === 'idle' || lower === 'n/a') {
    return false;
  }
  if (
    lower.includes('complete') ||
    lower.includes('success') ||
    lower === 'sections generated' ||
    lower.includes('error') ||
    lower.includes('failed') ||
    lower.includes('closed')
  ) {
    return false;
  }
  return true;
};

// Strip embedded percentage suffixes like " (30%)..." from status messages when the percentage is displayed in its own badge
export const formatGenerationStatusText = (
  status: string | undefined | null,
  isLocalLoading: boolean = false
): string => {
  if (isLocalLoading && (!status || !isServiceProcessingStatus(status))) {
    return 'Processing...';
  }
  const raw = (status || '').trim();
  if (!raw || raw.toLowerCase() === 'processing') {
    return 'Processing...';
  }
  const cleaned = raw.replace(/\s*\(\d{1,3}%\)\s*(\.{3})?$/, '$1').trim();
  return cleaned || 'Generating...';
};

// Extract embedded percentage from a status string like "Processing (45%)..." if numeric progress is not explicitly provided
export const extractProgressFromStatus = (
  status: string | undefined | null,
  explicitProgress?: number
): number | undefined => {
  if (typeof explicitProgress === 'number' && !Number.isNaN(explicitProgress)) {
    return Math.max(0, Math.min(100, Math.round(explicitProgress)));
  }
  if (!status) {
    return undefined;
  }
  const match = status.match(/\((\d{1,3})%\)/);
  if (match?.[1]) {
    const parsed = parseInt(match[1], 10);
    if (!Number.isNaN(parsed)) {
      return Math.max(0, Math.min(100, parsed));
    }
  }
  return undefined;
};

// Helper function to remove extension from filename
export const getFileNameWithoutExtension = (fileName: string): string => {
  if (!fileName) {
    return '';
  }
  const parts = fileName.split('.');
  if (parts.length === 1) {
    return fileName; // No extension
  }
  return parts.slice(0, -1).join('.');
};

// Helper function
export const displayDaysUntilDeadline = (
  daysUntilNumericOrError: number | string | null | undefined
): string => {
  if (
    daysUntilNumericOrError === null ||
    daysUntilNumericOrError === undefined ||
    typeof daysUntilNumericOrError === 'string'
  ) {
    return (daysUntilNumericOrError as string) || 'N/A';
  }
  const diffDays = daysUntilNumericOrError;
  if (diffDays === 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return '1 day';
  }
  if (diffDays < 0) {
    return 'Closed';
  }
  return `${diffDays} days`;
};

// Helper function to parse submissionDeadline string into a Date object
export const parseSubmissionDeadline = (deadlineStr: string): Date | null => {
  if (!deadlineStr) {
    return null;
  }
  // Example: "27/May/2025 02:00 PM (UTC+10:00) Brisbane time"
  const dateTimeRegex = /(\d{1,2})\/([A-Za-z]{3})\/(\d{4})\s*(\d{1,2}):(\d{2})\s*(AM|PM)/;
  const offsetRegex = /UTC([+-]\d{2}(?::?\d{2})?)/;

  const dateTimeMatch = deadlineStr.match(dateTimeRegex);
  const offsetMatch = deadlineStr.match(offsetRegex);

  if (!dateTimeMatch || !offsetMatch) {
    return null;
  }

  const [, dayStr, monthStr, yearStr, hourStr, minuteStr, ampm] = dateTimeMatch;
  let offset = offsetMatch[1]; // e.g., '+11', '+11:00', '+1100'
  if (offset && /^[+-]\d{2}$/.test(offset)) {
    offset += ':00';
  } else if (offset && /^[+-]\d{4}$/.test(offset)) {
    offset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
  }

  const monthMap = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };
  const month = monthMap[monthStr as keyof typeof monthMap];
  if (month === undefined) {
    return null;
  }

  let hour = parseInt(hourStr || '0', 10);
  if (ampm && ampm.toUpperCase() === 'PM' && hour !== 12) {
    hour += 12;
  } else if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) {
    // Midnight case e.g. 12 AM
    hour = 0;
  }

  // Construct ISO-like string: YYYY-MM-DDTHH:MM:SSZ or YYYY-MM-DDTHH:MM:SS+/-HH:MM
  const isoDateTimeString = `${yearStr}-${String(month + 1).padStart(2, '0')}-${String(dayStr).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minuteStr}:00${offset}`;

  const dateObj = new Date(isoDateTimeString);
  if (Number.isNaN(dateObj.getTime())) {
    return null;
  }
  return dateObj;
};

// NEW HELPER: Get a reliable Date object for the deadline
export const getDeadlineDate = (tender: Tender): Date | null => {
  if (!tender) {
    return null;
  }

  // Prioritize explicitly set deadlineDate (e.g., from user editing)
  if (tender.deadlineDate) {
    const explicitDeadline = new Date(tender.deadlineDate);
    if (!Number.isNaN(explicitDeadline.getTime())) {
      return explicitDeadline;
    }
  }

  let deadline: Date | null | undefined;

  // Check if submissionDeadline is a known invalid/placeholder string before attempting to parse
  const knownInvalidStrings = ['not specified', 'n/a'];
  if (
    tender.submissionDeadline &&
    !knownInvalidStrings.includes(tender.submissionDeadline.toLowerCase())
  ) {
    deadline = parseSubmissionDeadline(tender.submissionDeadline); // Uses the existing robust parser
  }

  if (deadline && !Number.isNaN(deadline.getTime())) {
    return deadline;
  }

  return null;
};

// Helper to calculate days until a given deadline date object
export const calculateDaysUntil = (deadlineDate: Date | null): number | string => {
  if (!deadlineDate || Number.isNaN(deadlineDate.getTime())) {
    return 'N/A'; // Or null, depending on how displayDaysUntilDeadline handles it
  }
  const today = new Date();
  const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const deadlineNormalized = new Date(
    deadlineDate.getFullYear(),
    deadlineDate.getMonth(),
    deadlineDate.getDate()
  );
  const diffTime = deadlineNormalized.getTime() - todayNormalized.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Helper function to render status with conditional styling and spinner
export const renderStatusIndicator = (
  status: string | undefined | null,
  theme: Theme
): React.ReactNode => {
  let style = {};
  let showSpinner = false;
  const statusText = status || 'N/A';
  const lowerCaseStatusText = statusText.toLowerCase(); // Convert to lowercase for case-insensitive checks

  if (lowerCaseStatusText.includes('complete')) {
    style = {
      backgroundColor: alpha(theme.palette.bidlogiq.orange, 0.5),
      color: theme.palette.text.primary,
      fontWeight: 'bold',
      padding: '2px 6px',
      borderRadius: '4px',
      display: 'inline-block',
      fontSize: '0.75rem', // Reduced font size
    };
    showSpinner = false; // Ensure spinner is off for Complete
  } else if (lowerCaseStatusText.includes('error')) {
    style = {
      backgroundColor: alpha(theme.palette.bidlogiq.rust, 0.5),
      color: theme.palette.bidlogiq.white,
      fontWeight: 'bold',
      padding: '2px 6px',
      borderRadius: '4px',
      display: 'inline-block',
      fontSize: '0.75rem', // Reduced font size
    };
    showSpinner = false; // Ensure spinner is off for Error
  } else if (statusText !== 'N/A') {
    style = {
      backgroundColor: theme.palette.bidlogiq.camel,
      color: theme.palette.text.primary,
      fontStyle: 'italic',
      padding: '2px 6px',
      borderRadius: '4px',
      display: 'inline-block',
      fontSize: '0.75rem', // Reduced font size
    };
    showSpinner = true; // Spinner is on for other active statuses
  } else {
    // N/A
    style = {
      padding: '2px 6px',
      borderRadius: '4px',
      display: 'inline-block',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      fontSize: '0.75rem', // Reduced font size
    };
    showSpinner = false; // No spinner for N/A
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {showSpinner && <CircularProgress size={12} />}
      <Typography component="span" sx={style}>
        {statusText}
      </Typography>
    </Box>
  );
};

// Helper function to format uploadedAt date properly
export const formatUploadedDate = (uploadedAt: UploadedAt | undefined): string => {
  if (!uploadedAt) {
    return 'N/A';
  }

  const formatWithTime = (date: Date) => {
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  try {
    // Handle Firestore Timestamp objects
    if (uploadedAt && typeof uploadedAt === 'object' && 'seconds' in uploadedAt) {
      // Convert Firestore Timestamp to JavaScript Date
      const date = new Date(uploadedAt.seconds * 1000);
      return formatWithTime(date);
    }

    // Handle ISO date strings and other string formats
    if (typeof uploadedAt === 'string') {
      const date = new Date(uploadedAt);
      if (!Number.isNaN(date.getTime())) {
        return formatWithTime(date);
      }

      // Handle timestamp strings that might be in different formats
      // Try parsing as a number (milliseconds since epoch)
      const timestampAsNumber = parseInt(uploadedAt, 10);
      if (!Number.isNaN(timestampAsNumber) && timestampAsNumber > 0) {
        // If it's a reasonable timestamp (after 1970 and before year 3000)
        if (timestampAsNumber > 0 && timestampAsNumber < 32503680000000) {
          const date = new Date(timestampAsNumber);
          if (!Number.isNaN(date.getTime())) {
            return formatWithTime(date);
          }
        }
      }

      // If it's a string that can't be parsed as a date, return it as-is
      // This handles backend failure strings like "processing failed" etc.
      return uploadedAt;
    }

    // Handle regular Date objects
    if (uploadedAt instanceof Date && !Number.isNaN(uploadedAt.getTime())) {
      return formatWithTime(uploadedAt);
    }

    // Handle numeric timestamps (milliseconds since epoch)
    if (typeof uploadedAt === 'number' && !Number.isNaN(uploadedAt) && uploadedAt > 0) {
      const date = new Date(uploadedAt);
      if (!Number.isNaN(date.getTime())) {
        return formatWithTime(date);
      }
    }

    return 'Invalid Date';
  } catch (_error) {
    return 'Invalid Date';
  }
};

export const getLoadingKey = (
  tenderId: string | undefined,
  action: string,
  docTypeOrFileName: string = ''
): string => `${tenderId || ''}_${action}_${docTypeOrFileName}`.replace(/[^a-zA-Z0-9_]/g, '_');

export const getDropzoneBoxStyle = (theme: Theme, isDragging: boolean): SxProps<Theme> => ({
  p: 2,
  border: `2px dashed ${isDragging ? theme.palette.bidlogiq.brick : theme.palette.greyContrast}`,
  borderRadius: theme.spacing(1),
  backgroundColor: isDragging ? theme.palette.bidlogiq.brick : theme.palette.greyLight,
  color: isDragging ? theme.palette.bidlogiq.white : theme.palette.text.primary,
  textAlign: 'center',
  width: '100%',
  // height: '100%' removed to prevent overflow with siblings
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer', // Indicate it's clickable/droppable
  transition:
    'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out',
  minHeight: '150px', // Increased minHeight
  maxHeight: '900px',
});

const formatTimestamp = (date: Date): string | null => {
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const extractTimestampFromFilename = (link: string): Date | null => {
  try {
    const url = new URL(link);
    const filename = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
    const decodedFilename = decodeURIComponent(filename).replace(/\.[^/.]+$/, '');

    // Support YYYYMMDD_HHMMSS
    const newMatch = decodedFilename.match(/_?(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/);
    if (newMatch && newMatch.length >= 7) {
      return new Date(
        Date.UTC(
          parseInt(newMatch[1] || '0', 10),
          parseInt(newMatch[2] || '0', 10) - 1,
          parseInt(newMatch[3] || '0', 10),
          parseInt(newMatch[4] || '0', 10),
          parseInt(newMatch[5] || '0', 10),
          parseInt(newMatch[6] || '0', 10)
        )
      );
    }

    // Support YYYYMMDDHHmm
    const match = decodedFilename.match(/_?(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})$/);

    if (!match || match.length < 6) {
      return null;
    }

    return new Date(
      Date.UTC(
        parseInt(match[1] || '0', 10),
        parseInt(match[2] || '0', 10) - 1,
        parseInt(match[3] || '0', 10),
        parseInt(match[4] || '0', 10),
        parseInt(match[5] || '0', 10)
      )
    );
  } catch {
    return null;
  }
};

// Helper function to extract timestamp from document link or fallback to lastUpdated/startedAt
export const getDocTimestampFromLink = (
  link: string | undefined,
  proc: { lastUpdated?: Date | string } | undefined
): string | null => {
  // Try to extract timestamp from filename in link
  if (link) {
    const date = extractTimestampFromFilename(link);
    if (date) {
      const formatted = formatTimestamp(date);
      if (formatted) {
        return formatted;
      }
    }
  }

  // Fallback to lastUpdated
  if (proc?.lastUpdated) {
    const formatted = formatTimestamp(toDate(proc.lastUpdated));
    if (formatted) {
      return formatted;
    }
  }

  return 'Unknown';
};
